import { useState, useEffect } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import {
  useCreateReporteProgrammatic,
  useUpdateReporteProgrammatic,
  useGruposDisponiblesProgrammatic,
} from '../hooks/useProgrammatic'
import type { ReporteProgrammatic, ReporteProgrammaticCreateInput, ReporteProgrammaticUpdateInput } from '../types'

interface ProgrammaticFormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reporte?: ReporteProgrammatic | null
}

const defaultFormData: ReporteProgrammaticCreateInput = {
  nombre: '',
  url: '',
  grupo_id: 0,
  orden: 0,
  activo: true,
}

export function ProgrammaticFormSheet({
  open,
  onOpenChange,
  reporte,
}: ProgrammaticFormSheetProps) {
  const [formData, setFormData] = useState<ReporteProgrammaticCreateInput>(defaultFormData)

  const { data: grupos, isLoading: gruposLoading } = useGruposDisponiblesProgrammatic()
  const createMutation = useCreateReporteProgrammatic()
  const updateMutation = useUpdateReporteProgrammatic()

  const isEditing = !!reporte

  useEffect(() => {
    if (!open) return

    if (reporte) {
      setFormData({
        nombre: reporte.nombre,
        url: reporte.url,
        grupo_id: reporte.grupo_id,
        orden: reporte.orden,
        activo: reporte.activo,
      })
    } else {
      setFormData(defaultFormData)
    }
  }, [open, reporte])

  const handleInputChange = (field: keyof ReporteProgrammaticCreateInput, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.nombre.trim() || !formData.url.trim() || !formData.grupo_id) {
      return
    }

    try {
      if (isEditing && reporte) {
        const payload: ReporteProgrammaticUpdateInput = { ...formData }
        await updateMutation.mutateAsync({ id: reporte.id, payload })
      } else {
        await createMutation.mutateAsync(formData)
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Error al guardar reporte:', error)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isEditing ? 'Editar Reporte' : 'Nuevo Reporte'}
          </SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* Grupo */}
          <div className="space-y-2">
            <Label htmlFor="grupo">Grupo *</Label>
            <Select
              value={formData.grupo_id ? formData.grupo_id.toString() : ''}
              onValueChange={(value) => handleInputChange('grupo_id', parseInt(value))}
              disabled={gruposLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un grupo" />
              </SelectTrigger>
              <SelectContent>
                {grupos?.map((grupo) => (
                  <SelectItem key={grupo.id} value={grupo.id.toString()}>
                    {grupo.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre del Reporte *</Label>
            <Input
              id="nombre"
              value={formData.nombre}
              onChange={(e) => handleInputChange('nombre', e.target.value)}
              placeholder="Ej: Dashboard Programmatic"
              required
            />
          </div>

          {/* URL */}
          <div className="space-y-2">
            <Label htmlFor="url">URL del Reporte *</Label>
            <Input
              id="url"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              placeholder="https://lookerstudio.google.com/embed/reporting/..."
              required
            />
          </div>

          {/* Orden */}
          <div className="space-y-2">
            <Label htmlFor="orden">Orden</Label>
            <Input
              id="orden"
              type="number"
              min={0}
              value={formData.orden ?? 0}
              onChange={(e) => handleInputChange('orden', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>

          <Separator />

          {/* Estado */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="activo">Reporte Activo</Label>
              <p className="text-sm text-muted-foreground">
                Los usuarios del grupo podrán ver este reporte
              </p>
            </div>
            <Switch
              id="activo"
              checked={formData.activo ?? true}
              onCheckedChange={(checked) => handleInputChange('activo', checked)}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isPending || !formData.nombre.trim() || !formData.url.trim() || !formData.grupo_id}
              className="bg-blue-800 hover:bg-blue-900 text-white"
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Guardar Cambios' : 'Crear Reporte'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
